import * as Sentry from "@sentry/react";

/**
 * Frontend logging utility that sends logs to Sentry
 * This provides a centralized way to log messages, errors, and debug info
 */
class Logger {
  private isEnabled: boolean;
  private environment: string;

  constructor() {
    this.environment = import.meta.env.VITE_FRONTEND_SENTRY_ENVIRONMENT || "development";
    this.isEnabled = import.meta.env.VITE_FRONTEND_ENABLE_SENTRY_LOGS === "true";
    
    // Log initialization status
    console.log("🔍 Frontend Logger Initialization:", {
      enabled: this.isEnabled,
      environment: this.environment,
      sentryDsn: import.meta.env.VITE_FRONTEND_SENTRY_DSN ? "✓ Provided" : "✗ Missing"
    });
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, extra?: Record<string, any>) {
    console.log(`[DEBUG] ${message}`, extra || {});
    
    if (this.isEnabled && this.environment === "development") {
      Sentry.addBreadcrumb({
        message,
        level: "debug",
        data: extra
      });
    }
  }

  /**
   * Log info messages
   */
  info(message: string, extra?: Record<string, any>) {
    console.log(`[INFO] ${message}`, extra || {});
    
    if (this.isEnabled) {
      Sentry.captureMessage(message, "info");
      if (extra) {
        Sentry.setContext("info_context", extra);
      }
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, extra?: Record<string, any>) {
    console.warn(`[WARN] ${message}`, extra || {});
    
    if (this.isEnabled) {
      Sentry.captureMessage(message, "warning");
      if (extra) {
        Sentry.setContext("warning_context", extra);
      }
    }
  }

  /**
   * Log error messages
   */
  error(message: string, error?: Error, extra?: Record<string, any>) {
    console.error(`[ERROR] ${message}`, error, extra || {});
    
    if (this.isEnabled) {
      if (error) {
        Sentry.captureException(error, {
          tags: { source: "frontend_logger" },
          extra: { message, ...extra }
        });
      } else {
        Sentry.captureMessage(message, "error");
      }
      
      if (extra) {
        Sentry.setContext("error_context", extra);
      }
    }
  }

  /**
   * Set user context for all subsequent logs
   */
  setUser(user: { id: string; email?: string; name?: string }) {
    if (this.isEnabled) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name
      });
    }
  }

  /**
   * Add custom tags for filtering
   */
  setTag(key: string, value: string) {
    if (this.isEnabled) {
      Sentry.setTag(key, value);
    }
  }

  /**
   * Test logging functionality
   */
  testLogging() {
    console.log("🧪 Testing frontend logging functionality...");
    
    this.debug("Debug test message", { testType: "debug", timestamp: new Date().toISOString() });
    this.info("Info test message", { testType: "info", timestamp: new Date().toISOString() });
    this.warn("Warning test message", { testType: "warning", timestamp: new Date().toISOString() });
    this.error("Error test message", new Error("Test error for logging validation"), { testType: "error", timestamp: new Date().toISOString() });
    
    console.log("✅ Frontend logging test completed - check Sentry dashboard for logs");
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for debugging in browser console
if (typeof window !== "undefined") {
  (window as any).frontendLogger = logger;
}